import { FieldHelperText, type FieldProps } from "@firecms/core";
import { BooleanSwitchWithLabel } from "@firecms/ui";

/**
 * Which way `true` reads for this property.
 *
 * Configurable rather than a single house style because the two collections
 * mean opposite things by it: `news.published: true` is good (the article is
 * live), `tags.retired: true` is a warning (the tag is hidden everywhere
 * public).
 */
export type StatusTone = "positive" | "negative";

const toneClass: Record<StatusTone, string> = {
    positive: "text-green-700 dark:text-green-400",
    negative: "text-red-700 dark:text-red-400",
};

export interface StatusSwitchProps {
    tone: StatusTone;
    /** Shown beside the switch when on. */
    onLabel: string;
    /** Shown beside the switch when off. */
    offLabel: string;
}

/**
 * A boolean field whose active state is colour-coded and named in words.
 *
 * The stock FireCMS switch renders the property name whatever the state, so
 * "Published" reads identically whether an article is live or not — the switch
 * position is the only signal. For the two flags that decide whether content is
 * publicly visible, that is too little emphasis.
 *
 * Wired through `property.Field` rather than by overriding FireCMS's CSS:
 * `BooleanProperty` carries no colour option and `BooleanSwitchWithLabel` takes
 * no `color` prop, and the tint has to differ per property — a stylesheet rule
 * would hit every boolean in the CMS at once.
 */
export function StatusSwitchField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    isSubmitting,
    includeDescription,
    customProps,
}: FieldProps<boolean, StatusSwitchProps>) {
    const { tone, onLabel, offLabel } = customProps;
    const active = value === true;

    return (
        <>
            <BooleanSwitchWithLabel
                value={active}
                onValueChange={setValue}
                error={showError}
                disabled={disabled || isSubmitting}
                position="start"
                label={
                    <span className="flex items-baseline gap-2">
                        <span>{property.name}</span>
                        <span
                            className={`text-sm font-medium ${active ? toneClass[tone] : "text-surface-500"}`}
                        >
                            {active ? onLabel : offLabel}
                        </span>
                    </span>
                }
            />

            <FieldHelperText
                includeDescription={includeDescription}
                showError={showError}
                error={error}
                property={property}
            />
        </>
    );
}
